// Catálogo de subtipos de consentimiento (Módulo H, Grupo 2 ampliado) — un
// solo lugar que enumera los 7 documentos de consentimiento del sistema
// (024 + los 6 nuevos), consumido por MotorFormularioConsentimiento.tsx
// (wizard genérico de firma), routes.tsx (una ruta estática por código, mismo
// patrón que FORMULARIOS_DISCRETOS) y ConsentimientosTab.tsx (lista +
// "generar nuevo"). Ver hallazgo de la auditoría de cumplimiento: "el sistema
// no distingue tipo de consentimiento" — cada entrada acá define el
// `subtipo` que construirConsentimientoInformado() graba en
// `Consent.category[1]`, además del LOINC 59284-0 genérico que ya llevaba el
// 024 (no se rompe lo existente).
import type { MedplumClient } from '@medplum/core';
import type { Consent, DocumentReference, Reference } from '@medplum/fhirtypes';
import { EXT_FIRMA_PACIENTE, EXT_FIRMA_REPRESENTANTE, EXT_REPRESENTANTE, extensionPersona, textoPersona, type PersonaConsentimiento } from './consentimiento024';

export interface ConsentimientoMeta {
  readonly codigo: string;
  readonly subtipo: string;
  readonly policyRuleText: string;
}

export const CONSENTIMIENTOS: readonly ConsentimientoMeta[] = [
  { codigo: '024', subtipo: 'consent-024', policyRuleText: 'AM 5316, Art. 21-26 — consentimiento informado del paciente' },
  { codigo: 'consent-quirurgico', subtipo: 'consent-quirurgico', policyRuleText: 'AM 5316 — consentimiento informado para procedimiento quirúrgico (documento separado del expediente prequirúrgico)' },
  { codigo: 'consent-anestesia', subtipo: 'consent-anestesia', policyRuleText: 'AM 5316 — consentimiento informado para anestesia (documento separado del expediente prequirúrgico)' },
  { codigo: 'consent-transfusion', subtipo: 'consent-transfusion', policyRuleText: 'AM 5316 — consentimiento informado para transfusión de hemoderivados (documento separado del expediente prequirúrgico)' },
  { codigo: 'consent-telesalud-canal', subtipo: 'consent-telesalud-canal', policyRuleText: 'AM 00044-2025 (Norma Técnica de Telesalud), Art. 7.2.2 — consentimiento del canal de telesalud' },
  { codigo: 'consent-telesalud-lopdp', subtipo: 'consent-telesalud-lopdp', policyRuleText: 'AM 00044-2025, Art. 7.2.2 y LOPDP (RO 459/2021) — tratamiento de datos personales específico de la sesión de telesalud' },
  {
    codigo: 'consent-voluntad-anticipada',
    subtipo: 'consent-voluntad-anticipada',
    policyRuleText: 'Sin normativa MSP/ACESS vigente a 2026 — documento de buena práctica clínica a pedido del paciente, no exigible legalmente',
  },
];

export const CODIGOS_CONSENTIMIENTO: readonly string[] = CONSENTIMIENTOS.map((c) => c.codigo);

export function metaConsentimiento(codigo: string): ConsentimientoMeta | undefined {
  return CONSENTIMIENTOS.find((c) => c.codigo === codigo);
}

/** Sistema local del segundo `category` del `Consent` FHIR — ver construirConsentimientoInformado en formulario/extractors.ts. */
export const SISTEMA_SUBTIPO_CONSENTIMIENTO = 'http://logiacare.ec/fhir/consent-subtipo';

/** Extensión que apunta al `Consent` original que un `Consent` de revocación deja sin efecto — ver construirRevocacion abajo. */
export const SISTEMA_EXTENSION_REVOCA = 'http://logiacare.ec/fhir/extension/revoca';

export interface DetalleRevocacion {
  /** Instante ISO de la revocatoria (fecha y hora que firmó el paciente); sin él, el de guardar. */
  dateTime?: string;
  /** PDF firmado de la revocatoria (bloque E del 024) — `Consent.sourceReference`, lo que "Ver / imprimir" abre en la pestaña. */
  documentReferencePdf?: Reference<DocumentReference>;
  /** Firma manuscrita de quien revoca; `representante` = firma por el paciente (menor de edad o incapaz). */
  firma?: { rol: 'paciente' | 'representante'; documentReference: Reference<DocumentReference> };
  representante?: PersonaConsentimiento;
}

/**
 * Revocación de un `Consent` (hallazgo de la auditoría de cumplimiento: "no
 * hay UI para revocar un Consent pese a exigirlo el art. 8 LOPDP"). Mismo
 * criterio create-only que el resto del sistema (ver
 * construirConsentimientoInformado en formulario/extractors.ts y el
 * hallazgo 2 de docs/consentimiento-telemedicina-msp-lopdp.md): la
 * revocación NUNCA edita el `Consent` original, crea uno nuevo con
 * `status: 'inactive'`, mismo `category`/`policyRule` (para que siga
 * agrupando bajo el mismo subtipo en ConsentimientosTab), y la extensión
 * `SISTEMA_EXTENSION_REVOCA` apuntando al original.
 */
export async function construirRevocacion(medplum: MedplumClient, original: Consent, detalle: DetalleRevocacion = {}): Promise<Consent> {
  if (!original.id) throw new Error('El consentimiento original no tiene id — no se puede revocar.');
  const extRepresentante = detalle.representante ? extensionPersona(EXT_REPRESENTANTE, detalle.representante) : undefined;
  return medplum.createResource<Consent>({
    resourceType: 'Consent',
    status: 'inactive',
    scope: original.scope,
    category: original.category,
    policyRule: original.policyRule,
    patient: original.patient,
    dateTime: detalle.dateTime ?? new Date().toISOString(),
    ...(detalle.documentReferencePdf ? { sourceReference: detalle.documentReferencePdf } : {}),
    extension: [
      { url: SISTEMA_EXTENSION_REVOCA, valueReference: { reference: `Consent/${original.id}` } },
      ...(detalle.firma ? [{ url: detalle.firma.rol === 'representante' ? EXT_FIRMA_REPRESENTANTE : EXT_FIRMA_PACIENTE, valueReference: detalle.firma.documentReference }] : []),
      ...(extRepresentante ? [extRepresentante] : []),
    ],
    ...(detalle.representante?.nombre
      ? { provision: { actor: [{ role: { text: 'Representante legal' }, reference: { display: textoPersona(detalle.representante) } }] } }
      : {}),
  });
}
